import { NavLink } from 'react-router-dom'
import { logout, signedInEmail } from '@/auth/session'

interface NavTab {
  id: 'documents' | 'suppliers' | 'businesses' | 'prompts' | 'upload'
  label: string
  path: string
}

const TABS: NavTab[] = [
  { id: 'documents', label: 'Documents', path: '/documents' },
  { id: 'suppliers', label: 'Suppliers', path: '/suppliers' },
  { id: 'businesses', label: 'Businesses', path: '/businesses' },
  { id: 'prompts', label: 'Prompts', path: '/prompts' },
  { id: 'upload', label: 'Upload', path: '/upload' },
]

/**
 * Navigation component with tabs for Documents, Suppliers, Businesses, and Upload pages.
 */
export function Navigation() {
  return (
    <nav className="bg-white border-b border-gray-200" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-end gap-3 py-2 text-sm">
          <span>{signedInEmail()}</span>
          <button type="button" onClick={logout}>Sign out</button>
        </div>
        <div className="flex space-x-8">
          {TABS.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.path}
              className={({ isActive }) =>
                `inline-flex items-center px-1 pt-1 pb-3 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
              data-testid={`nav-tab-${tab.id}`}
            >
              {tab.id === 'documents' ? (
                <DocumentIcon className="w-5 h-5 mr-2" />
              ) : tab.id === 'suppliers' ? (
                <SuppliersIcon className="w-5 h-5 mr-2" />
              ) : tab.id === 'businesses' ? (
                <BusinessesIcon className="w-5 h-5 mr-2" />
              ) : tab.id === 'prompts' ? (
                <PromptsIcon className="w-5 h-5 mr-2" />
              ) : (
                <UploadIcon className="w-5 h-5 mr-2" />
              )}
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>
    </nav>
  )
}

// Document icon component
function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  )
}

// Upload icon component
function UploadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
      />
    </svg>
  )
}

// Suppliers icon component
function SuppliersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  )
}

// Prompts icon component
function PromptsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
      />
    </svg>
  )
}

// Businesses icon component
function BusinessesIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  )
}

export default Navigation
